import React from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MdExpandMore, MdMenu } from 'react-icons/md';
import { motion } from 'framer-motion';
import Container from '../ui/Container';
import MobileMenu from './MobileMenu';
import { MAIN_NAV, PRODUCT_CATEGORIES } from '../../config/navigation';
import { SERVICES } from '../../data/services';
import SiesaLogo from '../ui/SiesaLogo';

const MENUS = {
  services: SERVICES.map(s => ({ label: s.title, href: '/services' })),
  products: [
    ...PRODUCT_CATEGORIES.map(c => ({
      label: c.label,
      href: `/products/${c.slug}`,
    })),
  ],
  projects: [
    { label: 'Galería de proyectos eléctricos', href: '/projects' },
    /* { label: 'Siesa QR App', href: '/siesa-qr-app' }, */
    { label: 'Ingeniería Digital & Software', href: '/tecnologia' },
  ],
};

function NavLink({ item, isActive }) {
  return (
    <Link
      as={RouterLink}
      to={item.href}
      variant="nav"
      px={{ lg: 2, xl: 3 }}
      py={2}
      fontSize={{ lg: 'xs', xl: 'sm' }}
      color={isActive ? 'accent.default' : 'text.primary'}
      position="relative"
      aria-current={isActive ? 'page' : undefined}
      whiteSpace="nowrap"
      _hover={{ textDecoration: 'none', color: 'accent.default' }}
    >
      {item.label}
      {isActive && (
        <Box
          as={motion.div}
          layoutId="activeNavLine"
          position="absolute"
          bottom="0"
          left={0}
          right={0}
          h="3px"
          bg="accent.default"
          borderRadius="full"
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        />
      )}
    </Link>
  );
}

function NavMenu({ item, isActive }) {
  const entries = MENUS[item.menu];
  return (
    <Menu placement="bottom-start" autoSelect={false} variant="publicSurface">
      <MenuButton
        as={Button}
        variant="text"
        px={{ lg: 2, xl: 3 }}
        fontSize={{ lg: 'xs', xl: 'sm' }}
        color={isActive ? 'accent.default' : 'text.primary'}
        rightIcon={<MdExpandMore aria-hidden="true" />}
        borderRadius={0}
        position="relative"
        whiteSpace="nowrap"
        _hover={{ textDecoration: 'none', color: 'accent.default' }}
      >
        {item.label}
        {isActive && (
          <Box
            as={motion.div}
            layoutId="activeNavLine"
            position="absolute"
            bottom="0"
            left={0}
            right={0}
            h="3px"
            bg="accent.default"
            borderRadius="full"
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          />
        )}
      </MenuButton>
      <MenuList
        bg="white"
        color="text.primary"
        py={2}
        boxShadow="lg"
        borderColor="border.default"
        borderRadius="lg"
        minW="250px"
        zIndex="dropdown"
      >
        {entries.map(entry => (
          <MenuItem
            key={entry.label}
            as={RouterLink}
            to={entry.href}
            fontSize="sm"
            bg="white"
            color="text.primary"
            minH="44px"
            _hover={{ bg: 'surface.blue', color: 'brand.700' }}
            _focus={{ bg: 'surface.blue', color: 'brand.700' }}
          >
            {entry.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

export default function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { pathname } = useLocation();

  const isActive = href =>
    href === '/' ? pathname === '/' : pathname.startsWith(href);

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex="sticky"
      bg="surface.default"
      borderBottom="1px solid"
      borderColor="border.default"
    >
      <Container>
        <Flex
          align="center"
          justify="space-between"
          h={{ base: '84px', lg: '104px' }}
          gap={{ lg: 2, xl: 4 }}
        >
          <Link
            as={RouterLink}
            to="/"
            aria-label="Soluciones Integraciones Eléctricas S.A., ir al inicio"
            flexShrink={0}
            _hover={{ textDecoration: 'none' }}
          >
            <SiesaLogo
              variant="navbar"
              h={{ base: '70px', sm: '84px' }}
              w="auto"
              maxH={{ base: '76px', sm: '90px' }}
              display={{ base: 'block', lg: 'none' }}
            />
            <Box
              display={{ base: 'none', lg: 'flex' }}
              flexDirection="column"
              lineHeight="1.05"
              py={1}
              _hover={{ opacity: 0.85 }}
              transition="opacity 0.2s"
            >
              <Text
                as="span"
                fontSize={{ lg: '0.85rem', xl: '1.05rem', '2xl': '1.2rem' }}
                fontWeight="900"
                color="brand.700"
                letterSpacing="0.03em"
                textTransform="uppercase"
              >
                Soluciones
              </Text>
              <Text
                as="span"
                fontSize={{ lg: '0.85rem', xl: '1.05rem', '2xl': '1.2rem' }}
                fontWeight="900"
                color="brand.900"
                letterSpacing="0.03em"
                textTransform="uppercase"
              >
                Integraciones
              </Text>
              <Text
                as="span"
                fontSize={{ lg: '0.85rem', xl: '1.05rem', '2xl': '1.2rem' }}
                fontWeight="900"
                color="brand.700"
                letterSpacing="0.04em"
                textTransform="uppercase"
              >
                Eléctricas S.A.
              </Text>
            </Box>
          </Link>

          <HStack
            as="nav"
            aria-label="Navegación principal"
            display={{ base: 'none', lg: 'flex' }}
            spacing={1}
          >
            {MAIN_NAV.map(item =>
              item.menu ? (
                <NavMenu
                  key={item.label}
                  item={item}
                  isActive={isActive(item.href)}
                />
              ) : (
                <NavLink
                  key={item.label}
                  item={item}
                  isActive={isActive(item.href)}
                />
              )
            )}
          </HStack>

          <HStack spacing={{ base: 2, lg: 1.5, xl: 2 }} flexShrink={0}>
            <Button
              as={RouterLink}
              to="/contact?subject=quote"
              variant="primary"
              size={{ base: 'md', lg: 'sm', xl: 'md' }}
              fontSize={{ base: 'sm', lg: 'xs', xl: 'sm' }}
              px={{ base: 4, lg: 2.5, xl: 4 }}
              display={{ base: 'none', md: 'inline-flex' }}
            >
              Solicitar cotización
            </Button>
            <IconButton
              aria-label="Abrir el menú de navegación"
              icon={<MdMenu size={24} />}
              variant="secondary"
              display={{ base: 'inline-flex', lg: 'none' }}
              onClick={onOpen}
            />
          </HStack>
        </Flex>
      </Container>

      <MobileMenu isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}
